import { Link, useLocation } from "react-router-dom";
import { FaExternalLinkAlt } from "react-icons/fa";
import { appendSearchToPath } from "src/customHooks/usePaginatedTableSearchParams";
import { SlotRequest, SlotRequestStatus } from "../types/slot-request.entity";

type Props = {
  data: (SlotRequest & { id: string })[];
};

const statusClass = (status: SlotRequestStatus) => {
  switch (status) {
    case "PENDING":
      return "border-amber-200 bg-amber-100 text-amber-800";
    case "ACCEPTED":
      return "border-emerald-200 bg-emerald-100 text-emerald-700";
    case "REJECTED":
      return "border-rose-200 bg-rose-100 text-rose-700";
    case "EXPIRED":
      return "border-slate-200 bg-slate-100 text-slate-700";
    case "CANCELLED":
      return "border-red-200 bg-red-100 text-red-700";
  }
};

const formatDateTime = (value: any) => {
  const date = value?.toDate?.() ?? (value instanceof Date ? value : null);
  if (!date) return "-";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const formatCurrency = (currency: string) => (currency === "KARMA" ? "K" : currency);

const userLinkClass =
  "inline-flex max-w-[180px] items-center rounded border px-2 py-1 font-mono text-[10px] font-semibold no-underline transition-colors";

export const SlotRequestsTable = ({ data }: Props) => {
  const { search } = useLocation();

  return (
    <div className="mb-6 overflow-x-auto border border-slate-200 bg-white shadow-sm">
      <table className="min-w-[1160px] w-full divide-y divide-slate-200 text-left text-xs">
        <thead className="bg-slate-900 text-[10px] uppercase tracking-wide text-white">
          <tr>
            <th className="px-4 py-3">Request</th>
            <th className="px-4 py-3">Participants</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Reserved</th>
            <th className="px-4 py-3">Requested for</th>
            <th className="px-4 py-3">Created</th>
            <th className="px-4 py-3">Outcome</th>
            <th className="px-4 py-3 text-center">Open</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {data.length === 0 && (
            <tr>
              <td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-500">
                No slot requests match these filters.
              </td>
            </tr>
          )}

          {data.map((request) => (
            <tr key={request.id} className="odd:bg-white even:bg-slate-50 hover:bg-emerald-50/40">
              <td className="px-4 py-3 font-mono text-[11px] text-slate-700" title={request.id}>
                {request.id}
              </td>
              <td className="px-4 py-3">
                <div className="flex min-w-[220px] flex-col gap-2">
                  <Link
                    to={`/users/${request.learnerId}`}
                    className={`${userLinkClass} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
                    title="Open learner"
                  >
                    <span className="mr-2 text-[9px] uppercase text-emerald-600">Learner</span>
                    <span className="truncate">{request.learnerId}</span>
                  </Link>
                  <Link
                    to={`/users/${request.coachId}`}
                    className={`${userLinkClass} border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
                    title="Open coach"
                  >
                    <span className="mr-2 text-[9px] uppercase text-blue-600">Coach</span>
                    <span className="truncate">{request.coachId}</span>
                  </Link>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex rounded-full border px-2 py-1 text-[10px] font-semibold ${statusClass(request.status)}`}>
                  {request.status}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="font-semibold text-slate-800">
                  {request.money.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} {formatCurrency(request.money.currency)}
                </div>
                <div className="text-[10px] text-slate-500">Balance reserved</div>
              </td>
              <td className="px-4 py-3 text-slate-700">{formatDateTime(request.requestedDate)}</td>
              <td className="px-4 py-3 text-slate-700">{formatDateTime(request.createdAt)}</td>
              <td className="px-4 py-3">
                <div className="flex min-w-[160px] flex-col gap-1 text-[10px]">
                  {request.bookingId ? (
                    <Link to={`/bookings/${request.bookingId}`} className="font-mono text-emerald-700 no-underline hover:underline">
                      Booking: {request.bookingId}
                    </Link>
                  ) : null}
                  {request.slotId ? (
                    <Link to={`/slots/${request.slotId}`} className="font-mono text-sky-700 no-underline hover:underline">
                      Slot: {request.slotId}
                    </Link>
                  ) : null}
                  {!request.bookingId && !request.slotId ? <span className="text-slate-400">No linked booking or slot</span> : null}
                </div>
              </td>
              <td className="px-4 py-3 text-center">
                <Link
                  to={appendSearchToPath(`/slot-requests/${request.id}`, search)}
                  title="Open slot request"
                  aria-label={`Open slot request ${request.id}`}
                  className="inline-flex h-9 w-9 items-center justify-center bg-emerald-600 text-white transition-colors hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2"
                >
                  <FaExternalLinkAlt />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SlotRequestsTable;
